import { AssigneeType } from '@heidi/schema/ml-scribe';

// Team assignment nulls `assignee_id`; identity lives on `assignee.id`.
type AssignableTask = {
  id?: string | null;
  user_id?: string | null;
  assignee_id?: string | null;
  assignee_name?: string | null;
  assignee_avatar_url?: string | null;
  assignee?: { id?: string | null; type?: AssigneeType | null } | null;
};

export type ClinicalTaskEditFacts = {
  currentUserId?: string | null;
  createdByUserId?: string | null;
  joinedTeamIds?: readonly string[] | null;
  hasSessionEdit?: boolean;
};

export type ClinicalTaskUserOwner = {
  id: string;
  name?: string | null;
  avatarUrl?: string | null;
};

export const resolveClinicalTaskAssigneeId = (
  task: AssignableTask | null | undefined
): string | null => task?.assignee?.id ?? task?.assignee_id ?? null;

export const isClinicalTaskUnassigned = (
  task: AssignableTask | null | undefined
): boolean => resolveClinicalTaskAssigneeId(task) === null;

const isAssignedToCurrentUser = (
  task: AssignableTask,
  currentUserId: string | null | undefined
): boolean => {
  if (!currentUserId) return false;
  if (task.assignee?.type === AssigneeType.team) return false;
  return resolveClinicalTaskAssigneeId(task) === currentUserId;
};

const isAssignedToJoinedTeam = (
  task: AssignableTask,
  joinedTeamIds: readonly string[] | null | undefined
): boolean => {
  if (task.assignee?.type !== AssigneeType.team || !joinedTeamIds?.length) {
    return false;
  }
  const teamId = resolveClinicalTaskAssigneeId(task);
  return teamId != null && joinedTeamIds.includes(teamId);
};

export const canEditClinicalTask = (
  task: AssignableTask | null | undefined,
  facts: ClinicalTaskEditFacts
): boolean => {
  if (!task) return false;
  const currentUserId = facts.currentUserId;
  if (currentUserId && facts.createdByUserId && currentUserId === facts.createdByUserId) {
    return true;
  }
  if (isAssignedToCurrentUser(task, currentUserId)) return true;
  if (isAssignedToJoinedTeam(task, facts.joinedTeamIds)) return true;
  return facts.hasSessionEdit === true;
};

export const canStartHeidiOnClinicalTask = canEditClinicalTask;

export const withTaskAssignedToUser = <T extends AssignableTask>(
  task: T,
  taskId: string,
  owner: ClinicalTaskUserOwner
): T => {
  if (task.id !== taskId) return task;
  return {
    ...task,
    assignee_id: owner.id,
    assignee: { id: owner.id, type: AssigneeType.user },
    assignee_name: owner.name ?? null,
    assignee_avatar_url: owner.avatarUrl ?? null,
  };
};
